import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {NotificationManager} from '../components/common/react-notifications';
import {adminRoot, defaultColor, messageDismissTime} from '../constants/defaultValues';
import {
  CONNECTED, DR_EVENT_UTILITY, MESSAGES_FROM_SERVER, ONLINE, ROLES, SCHEME_TYPES,
} from '../constants/portalsConstants';
import {usStateAbbrs} from '../constants/usStates';
import {checkForStartChargingError, formatStationInfo, getStationType} from '../helpers/dashboardHelper';
import {
  formatDREventTime,
  formatSitePayment,
  formatUserStateInfo,
  getCurrentUser,
  removeCurrentUser,
  setCurrentColor,
  setCurrentUser,
} from '../helpers/Utils';
import {
  FETCH_ERROR_CODE,
  checkSiteAccess,
  parseApiError,
  formatErrorForMiddleware,
} from './apiHelper';
import {
  setSessionHasExpired,
  setSessionHasStarted,
  setSessionIsLoggingOut,
} from './userSlice';

const {REACT_APP_PORTALS_API} = window.env;

// Tags
const USER = 'User';
const STATION = 'Station';
const SITE = 'Site';
const CHARGE_DATA = 'ChargeData';

export const oldPasswordIncorrectError = 'Old password incorrect';

// Reusable methods
const logoutPost = async (sendRequest, api, extraOptions) => {
  await sendRequest({
    url: 'logout',
    method: 'POST',
  }, api, extraOptions);
};

const baseQuery = fetchBaseQuery({
  baseUrl: REACT_APP_PORTALS_API,
  credentials: 'include',
});
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const {dispatch} = api;
  const {data, error} = await baseQuery(args, api, extraOptions);
  if (error) {
    const parsedError = parseApiError(error);
    if (parsedError?.status === 401) {
      if (parsedError?.data?.reason === MESSAGES_FROM_SERVER.REQUESTING_REFRESH_TOKEN) {
        const {data: refreshTokenData, error: refreshTokenError} = await baseQuery(
          {
            url: '/refresh',
            method: 'POST',
          },
          api,
          extraOptions,
        );
        if (refreshTokenError) {
          const parsedRefreshTokenError = parseApiError(refreshTokenError);
          if (parsedRefreshTokenError?.status !== 200) {
            removeCurrentUser();
            setCurrentColor(defaultColor);
            dispatch(setSessionHasExpired());
            return false;
          }
        }
        // store the new token
        const {userInfo} = refreshTokenData;
        const userObject = {
          id: userInfo.id,
          activated: userInfo.activated,
        };

        setCurrentUser(userObject);
        // retry the initial query
        const result = await baseQuery(args, api, extraOptions);
        return result;
      }
      return formatErrorForMiddleware(parsedError);
    }
  }
  return {data, error};
};

const authEndpoints = builder => ({
  activateUser: builder.mutation({
    async queryFn({user, history}, _, __, sendRequest) {
      const {
        firstName,
        lastName,
        password,
        phone: cellPhone,
        token,
        email: username,
      } = user;
      const {data, error} = await sendRequest({
        url: 'admin/activate?environment=driver',
        method: 'POST',
        body: {
          firstName,
          lastName,
          password,
          cellPhone,
          token,
          username,
        },
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (error) return {error: error.message};
      if (data.error) return {error: data.error};
      if (data.data?.error) return {error: data.data.error};
      NotificationManager.success(
        'Account successfully activated',
        'Thank you',
        messageDismissTime,
        null,
        null,
        null,
        true,
      );
      history.push(adminRoot);
      return {data: null};
    },
  }),
  login: builder.mutation({
    queryFn: async ({credentials, history, from}, endpointApi, __, sendRequest) => {
      const {dispatch} = endpointApi;
      const {data, error} = await sendRequest({
        url: 'auth',
        method: 'POST',
        body: {
          username: credentials.email,
          password: credentials.password,
          staySignedIn: !!credentials.staySignedIn,
        },
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (data.error) return {error: data.error};
      const {userInfo} = data;
      const userObject = {
        id: userInfo.id,
        activated: userInfo.activated,
      };

      setCurrentUser(userObject);
      dispatch(setSessionHasStarted());
      // redirect priority:
      // manually-declared (from) > station > home
      if (history && from) {
        history.push(from);
      } else if (history && userInfo.currChargingStation) {
        history.push(`${adminRoot}/station/${userInfo.currChargingStation}`);
      } else if (history) {
        history.push('/');
      }

      NotificationManager.persistentSuccess('Welcome', 'Login Success', messageDismissTime);
      return {data: true};
    },
    invalidatesTags: [USER, STATION, SITE, CHARGE_DATA],
  }),
  logout: builder.mutation({
    async queryFn(_, endpointApi, ___, sendRequest) {
      const {dispatch} = endpointApi;
      await logoutPost(sendRequest, endpointApi, null);
      removeCurrentUser();
      setCurrentColor(defaultColor);
      dispatch(setSessionIsLoggingOut());
    },
  }),
  forgotPassword: builder.mutation({
    async queryFn(email, _, __, sendRequest) {
      const {error} = await sendRequest({
        url: 'auth/forgot?environment=driver',
        method: 'POST',
        body: {email},
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (data.error) return {error: data.error};
      if (error) return {error: error.message};
      NotificationManager.success('Please check your email for instructions.', 'Success', messageDismissTime);
      return {data: null};
    },
  }),
  resetPassword: builder.mutation({
    async queryFn({history, ...body}, _, __, sendRequest) {
      const {data, error} = await sendRequest({
        url: 'auth/reset?environment=driver',
        method: 'POST',
        headers: {Authorization: `Bearer ${body.token}`},
        body,
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (error) return {error: error.message};
      if (data.error) return {error: data.error};
      NotificationManager.success('Password reset successfully.', 'Success', messageDismissTime, null, null, null, true);
      history.push('/');
      return {data: null};
    },
  }),
  updatePassword: builder.mutation({
    async queryFn(body, _, __, sendRequest) {
      const {data, error} = await sendRequest({
        url: 'auth/reset',
        method: 'POST',
        body: {
          ...body,
          token: '',
        },
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (data?.error === 'Temporary password incorrect') {
        return {error: oldPasswordIncorrectError, skipMiddleware: true};
      }
      if (error || data.error) return {error: 'Password could not be updated.'};

      NotificationManager.success('Password updated successfully.', 'Success');
      return {data: null};
    },
  }),
  getSupport: builder.query({
    query: params => ({
      url: 'support',
      params,
    }),
  }),
  sendSupport: builder.mutation({
    async queryFn({history, ...body}, _, __, sendRequest) {
      const {error} = await sendRequest({
        url: 'support',
        method: 'POST',
        body,
      });
      if (error) return {error: "We're sorry, we could not submit your support ticket at this time. Please contact support directly using the phone number or email above the support form. We apologize for the inconvenience."};
      NotificationManager.persistentSuccess(
        'Support email successfully sent ',
        'Thank you',
        messageDismissTime,
      );
      history.goBack();
      return {data: null};
    },
  }),
});

const siteAndStationEndpoints = builder => ({
  getStation: builder.query({
    providesTags: (_res, _err, {stationId}) => [{type: STATION, id: stationId}],
    queryFn: async (stationId, {getState}, __, sendRequest) => {
      const {data: station, error: getStationError} = await sendRequest(`station/${stationId}`);
      if (getStationError) return formatErrorForMiddleware(getStationError, {resource: 'station'});

      const {data, error} = await checkSiteAccess(station, getState().api.queries, sendRequest);
      if (error) return {error};
      const userId = getCurrentUser()?.id;
      const {
        assignedUser,
        costPerHour,
        costPerKwh,
        driverLoginReq,
        id,
        info,
        isCharging,
        name,
        paymentRequired,
        site,
        siteId,
      } = data;

      const currentStation = {
        charging: isCharging,
        connected: info.connection === CONNECTED,
        driverLoginReq,
        id,
        name,
        online: info.status === ONLINE,
        showDrEvents: site.utility.utilityName === DR_EVENT_UTILITY,
        siteId,
        siteName: site.name,
        // ideally for convenience/readability
        type: getStationType(driverLoginReq, paymentRequired),
        userIsAssignedUser: userId === assignedUser,
      };

      const customerUrl = station?.site?.customer?.url;
      if (customerUrl) {
        const {
          data: customer,
          error: getCustomerError,
        } = await sendRequest(`customer?name=${customerUrl}`);
        if (getCustomerError) return formatErrorForMiddleware(getCustomerError, {resource: 'station'});
        if (!customer.length) return {error: 'Customer not found'};

        // for Site CTL info modal
        currentStation.customerName = customer[0].name;

        if (paymentRequired) {
          if (getCustomerError || !customer.length) return formatErrorForMiddleware();
          // False if collect account is set up. Drivers shouldn't be able to charge in this case.
          currentStation.paymentChargingDisabled = !customer[0].collectionAcctId;
          currentStation.cost = costPerHour || costPerKwh;
          currentStation.uom = costPerHour ? '/hr' : '/kWh';
        }
      }

      return {data: currentStation};
    },
  }),
  getAllowedSites: builder.query({
    providesTags: [{type: SITE, id: 'LIST'}],
    queryFn: async (_, __, ___, sendRequest) => {
      const {data, error} = await sendRequest('site');
      if (error) return formatErrorForMiddleware(error, {resource: 'sites'});

      const {value: sitesInfo} = data;
      if (!sitesInfo.length) return {data: []};
      const sites = sitesInfo.length > 1 ? sitesInfo.slice(0, -1) : sitesInfo;

      return {
        data: sites.map(site => ({
          id: site.id,
          location: `${site.city}, ${usStateAbbrs[site.state.toLowerCase()]} ${site.zipcode}`,
          name: site.name,
          stationsOnline: site.siteStationsOnline,
          streetAddress: site.address,
          driverSupportEmail: site.driverSupportEmail,
          driverSupportPhone: site.driverSupportPhone,
          // This query gets result like {sessionSchemes: [{type: 'connectionTimeLimits', value: x}}]
          timeLimit: site.sessionSchemes?.find(scheme => scheme.type === SCHEME_TYPES.CTL)?.value,
          totalStations: site.siteTotalStations,
          ...formatSitePayment(site),
        })),
      };
    },
  }),
  getSite: builder.query({
    providesTags: (_res, _err, id) => [{type: SITE, id}],
    queryFn: async (siteId, {getState}, _, sendRequest) => {
      const {data, error: siteError} = await sendRequest(`site/${siteId}`);
      if (siteError) return formatErrorForMiddleware(siteError, {resource: 'site'});

      const {data: site, error} = await checkSiteAccess(data, getState().api.queries, sendRequest);
      if (error) return {error};

      return {
        data: {
          id: site.id,
          name: site.name,
          showDrEvents: site.utility.utilityName === DR_EVENT_UTILITY,
          stations: site.stations,
          // The query gets a result like {sessionScheme: {connectionTimeLimit: {value: x}}}
          timeLimit: site.sessionScheme?.connectionTimeLimit?.value,
          ...formatSitePayment(site),
        },
      };
    },
  }),
  getChargingData: builder.query({
    async queryFn(stationId, _, __, sendRequest) {
      const formatEndpoint = str => `station/${stationId}/${str}`;
      const dataEndpoint = formatEndpoint('data');
      const currentSessionEndpoint = formatEndpoint('currentSession');

      const {data: deviceData, error: dataErr} = await sendRequest({
        url: dataEndpoint,
        params: {chargeStatus: 'chargeStatus'},
      });
      if (dataErr?.status === 401 || dataErr?.originalStatus === 401) {
        return formatErrorForMiddleware(dataErr, {resource: 'station'});
      }

      const {data: csData, error: csErr} = await sendRequest(currentSessionEndpoint);
      if (csErr?.status === 401 || csErr?.originalStatus === 401) {
        return formatErrorForMiddleware(csErr, {resource: 'station'});
      }
      const userId = getCurrentUser()?.id;
      const chargingData = formatStationInfo(userId, deviceData || {}, csData || {});

      const {data: ctlData, error: ctlErr} = await sendRequest({
        url: dataEndpoint,
        params: {connectionTimeLimits: true},
      });

      if (ctlErr?.status === 401 || ctlErr?.originalStatus === 401) {
        return formatErrorForMiddleware(ctlErr, {resource: 'station'});
      }

      // what do we want to do with errors here? I feel like it's not the best
      // UX to potentially show a notification every 10 seconds

      if (ctlData?.sessionSchemes?.connectionTimeLimit) {
        chargingData.timeLimit = ctlData.sessionSchemes.connectionTimeLimit.value;
        // taken from how portals calculates time remaining (kitu-portals/server/api/controllers/site/{id}.js)
        chargingData.timeLimitRemaining = ctlData.limitMetTime ? 0 : Math.max(chargingData.timeLimit - ctlData.secondsUsedIncludingCurrentSession, 0);

        // if the station being observed already has an assigned user and if the current logged in user is not the
        // same user as the assigned user display the site's overall CTL info
        if (deviceData.assignedUser && userId !== deviceData.assignedUser) {
          chargingData.timeLimitRemaining = ctlData.sessionSchemes.connectionTimeLimit.value;
        }
      }

      return {data: chargingData};
    },
    providesTags: (_res, _err, stationId) => [
      USER,
      {type: CHARGE_DATA, id: stationId},
    ],
  }),
  startCharging: builder.mutation({
    async queryFn({
      customerId, stationId, userId, token,
    }, _, __, sendRequest) {
      const request = {
        url: `station/${stationId}/startCharging`,
        method: 'POST',
        body: {id: userId},
      };
      if (token) request.headers = {Authorization: `Bearer ${token}`};
      const {data, error} = await sendRequest(request);
      if (error?.status === FETCH_ERROR_CODE) return {error};
      // Portals can send error object as 200
      const maybeError = checkForStartChargingError(data || error);
      if (maybeError) return {error: {...maybeError, skipMiddleware: true}};
      try {
        // only passed in during publicpayment, send signup email in background
        if (customerId) {
          await sendRequest({
            url: 'admin/sendSignupEmail',
            method: 'POST',
            body: {userId, customerId},
          });
        }
      } catch (err) { /* */ }
      return {data: {}};
    },
  }),
  openNotifications: builder.mutation({
    query: ({type, id, ...body}) => ({
      url: `${type}/${id}/openNotification`,
      method: 'POST',
      body,
    }),
  }),
});

const userEndpoints = builder => ({
  getUser: builder.query({
    async queryFn(_, __, ___, sendRequest) {
      const currentUser = getCurrentUser();
      const id = currentUser?.id;
      if (!id) return {data: {}};

      const {data: user, error} = await sendRequest(`admin/${id}`);
      if (error?.status === 'FETCH_ERROR') return {error};
      if (error) return {error: error.message};
      const {data: currentSession} = await sendRequest(`admin/${id}/currentSession`);

      // gets the station the user is currently charging at
      user.currChargingStation = currentSession?.stationId || null;

      // If charging at payment station, cannot modify payment in
      user.canModifyPayment = !currentSession?.paymentRequired;

      const formattedData = formatUserStateInfo(user);

      // Should update portals to do this automatically, maybe w a query param?
      if (user.customerId) {
        try {
          const {data: {customerLogoDataURL}} = await sendRequest({
            url: `customer/${user.customerId}`,
            params: {ignoreSites: true},
          });
          if (customerLogoDataURL) {
            formattedData.customerLogoDataURL = customerLogoDataURL;
            // Could potentially create a separate object for this
            // But that seems more tedious to maintain
            currentUser.customerLogoDataURL = customerLogoDataURL;
            setCurrentUser(currentUser);
          }
        } catch (err) {
          // Ignoring errors here, if it's just cuz we can't get logo, no big deal
          console.warn('Could not fetch customer logo'); // eslint-disable-line no-console
        }
      }
      formattedData.isAdmin = user.roleId <= ROLES.SITE_ADMIN;

      return {data: formattedData};
    },
    providesTags: [USER],
  }),
  updateUser: builder.mutation({
    async queryFn(body, _, __, sendRequest) {
      const {error} = await sendRequest({
        url: `admin/${body.id}`,
        method: 'PATCH',
        body,
      });
      if (error) return {error};
      NotificationManager.success('User information updated', 'Success', messageDismissTime);
      return {data: null};
    },
    invalidatesTags: [USER],
  }),
  createPublicUser: builder.mutation({
    query: body => ({
      url: 'admin/publicUser',
      method: 'POST',
      body,
    }),
  }),
  sendSignupEmail: builder.mutation({
    query: body => ({
      url: 'admin/sendSignupEmail',
      method: 'POST',
      body,
    }),
  }),
});

const paymentEndpoints = builder => ({
  updateBilling: builder.mutation({
    /* eslint-disable-next-line complexity */
    async queryFn(params, _, __, sendRequest) {
      // for thrown errors, should be set before each async attempt
      let message = 'Not logged in';
      const {
        deleteFirst, stripeToken, billingObject, navCallback,
      } = params;
      const {receiptPreference, ...addressParams} = billingObject;
      const userId = getCurrentUser().id;
      const userEndpoint = `admin/${userId}`;
      const driverAccountEndpoint = `${userEndpoint}/driverAccount`;

      if (deleteFirst && stripeToken) {
        message = 'Could not delete card.';
        const {data: deleteResponse, error: deleteError} = await sendRequest({
          url: driverAccountEndpoint,
          method: 'DELETE',
        });
        if (deleteError) return {error: 'Failure to delete existing card'};
        if (deleteResponse.message) return {error: deleteResponse.message};
      }

      const body = {receiptPreference};
      if (stripeToken) {
        body.token = stripeToken;
        Object.assign(body, addressParams);
        message = 'Failed to add card';
        const {data: patchResponse, error: patchError} = await sendRequest({
          url: userEndpoint,
          method: 'PATCH',
          body,
        });
        if (patchError) return {error: message};
        if (patchResponse.message && !patchResponse.userInfo) {
          return {error: patchResponse.message};
        }
      } else {
        message = 'Failed to update billing address';

        const {data: addressResponse, addressError} = await sendRequest({
          url: driverAccountEndpoint,
          method: 'PATCH',
          body: {card: addressParams},
        });

        if (addressError) return {error: message};
        if (addressResponse.message) return {error: addressResponse.message};

        const {data: patchResponse, error: patchError} = await sendRequest({
          url: userEndpoint,
          method: 'PATCH',
          body: {receiptPreference},
        });
        if (patchError) return {error: message};
        if (patchResponse.message && !patchResponse.userInfo) {
          return {error: patchResponse.message};
        }
      }

      if (navCallback) navCallback();
      NotificationManager.success('Payment information updated.', 'Success', messageDismissTime);
      return {data: null};
    },
    invalidatesTags: [USER],
  }),
  deleteCard: builder.mutation({
    async queryFn(userId, _, __, sendRequest) {
      const {data: {message}, error} = await sendRequest({
        url: `admin/${userId}/driverAccount`,
        method: 'DELETE',
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (error) return {error: null};
      if (message) return {error: message};
      NotificationManager.success('Card deleted', 'Success', messageDismissTime);
      return {data: null};
    },
    invalidatesTags: [USER],
  }),
  payPastDueBill: builder.mutation({
    async queryFn({billId, history}, _, __, sendRequest) {
      const userId = getCurrentUser().id;
      if (!userId) {
        history.push('/');
        return {error: 'No user information found during payment attempt. Your login may have expired. Please log in and try again.'};
      }

      const {data, error} = await sendRequest({
        url: `admin/${userId}/payPastDue`,
        method: 'POST',
        body: {
          billId,
          userId,
        },
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      const maybeError = data?.isStripeError ? data : error;
      if (maybeError) return {error: maybeError.message};
      NotificationManager.success('Outstanding bill paid.', 'Success');
      return {data: null};
    },
    invalidatesTags: [USER],
  }),
  addPublicPaymentCard: builder.mutation({
    async queryFn({driverId, token, stripeToken}, _, __, sendRequest) {
      const {data, error} = await sendRequest({
        url: `admin/${driverId}`,
        method: 'PATCH',
        headers: {Authorization: `Bearer ${token}`},
        body: {token: stripeToken},
      });
      if (error?.status === FETCH_ERROR_CODE) return {error};
      if (error) return {error: error.message};
      if (data.message && !data.userInfo) return {error: data.message};
      return {data: !!data.userInfo.card};
    },
  }),
});

const utilityEndpoints = builder => ({
  getDREvents: builder.query({
    queryFn: async (_, __, ___, sendRequest) => {
      const {data, error} = await sendRequest('drevents');
      if (error) return {error: {skipMiddleware: true}};
      return {data};
    },
    transformResponse: formatDREventTime,
  }),
});

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [USER, STATION, SITE, CHARGE_DATA],
  endpoints: builder => ({
    ...authEndpoints(builder),
    ...siteAndStationEndpoints(builder),
    ...userEndpoints(builder),
    ...paymentEndpoints(builder),
    ...utilityEndpoints(builder),
  }),
});

export const {
  // gets
  useGetAllowedSitesQuery,
  useGetChargingDataQuery,
  useGetDREventsQuery,
  useGetSiteQuery,
  useGetStationQuery,
  useGetSupportQuery,
  useGetUserQuery,
  // mutations
  useActivateUserMutation,
  useAddPublicPaymentCardMutation,
  useCreatePublicUserMutation,
  useDeleteCardMutation,
  useForgotPasswordMutation,
  useLoginMutation,
  useLogoutMutation,
  useOpenNotificationsMutation,
  usePayPastDueBillMutation,
  useResetPasswordMutation,
  useSendSignupEmailMutation,
  useSendSupportMutation,
  useStartChargingMutation,
  useUpdateUserMutation,
  useUpdatePasswordMutation,
  useUpdateBillingMutation,
  useRefreshTokenServiceMutation,
} = api;
